import { apiClient } from './client';
import type { SeatingVisualizationPayload } from '@/types/seating-v2.types';

export interface GenerateLayoutRequest {
  seatingConfigId: number;
  configType: string;
  params: Record<string, number>;
}

export interface AssignGuestRequest {
  guestId: number;
  positionId: number;
}

export interface BulkAssignRequest {
  assignments: AssignGuestRequest[];
}

/**
 * API service for seating visualization and guest assignments
 */
export const seatingApi = {
  /**
   * Generate seating layout for an event
   */
  generateLayout: async (eventId: number, request: GenerateLayoutRequest) => {
    const response = await apiClient.post(
      `/events/${eventId}/seating/generate`,
      request
    );
    return response.data;
  },

  /**
   * Get visualization data (layered architecture — HTML/CSS rendering)
   */
  getVisualizationV2: async (eventId: number): Promise<{ success: boolean; data: SeatingVisualizationPayload }> => {
    const response = await apiClient.get(
      `/events/${eventId}/seating/visualization-v2`
    );
    return response.data;
  },

  /**
   * Get all seating positions
   */
  getPositions: async (eventId: number, type?: string) => {
    const url = type
      ? `/events/${eventId}/seating/positions?type=${type}`
      : `/events/${eventId}/seating/positions`;
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Get available (unassigned) positions
   */
  getAvailablePositions: async (eventId: number) => {
    const response = await apiClient.get(
      `/events/${eventId}/seating/positions/available`
    );
    return response.data;
  },

  /**
   * Get all guest assignments
   */
  getAssignments: async (eventId: number) => {
    const response = await apiClient.get(
      `/events/${eventId}/seating/assignments`
    );
    return response.data;
  },

  /**
   * Assign a guest to a position
   */
  assignGuest: async (eventId: number, guestId: number, positionId: number) => {
    const response = await apiClient.post(
      `/events/${eventId}/seating/assign`,
      { guestId, positionId }
    );
    return response.data;
  },

  /**
   * Bulk assign guests to positions
   */
  bulkAssignGuests: async (eventId: number, assignments: AssignGuestRequest[]) => {
    const response = await apiClient.post(
      `/events/${eventId}/seating/assign-bulk`,
      { assignments }
    );
    return response.data;
  },

  /**
   * Remove guest assignment
   */
  removeAssignment: async (eventId: number, guestId: number) => {
    const response = await apiClient.delete(
      `/events/${eventId}/seating/assignments/${guestId}`
    );
    return response.data;
  },

  /**
   * Get occupancy/capacity data
   */
  getOccupancy: async (eventId: number) => {
    const response = await apiClient.get(
      `/events/${eventId}/seating/occupancy`
    );
    return response.data;
  },

  /**
   * Get seating layout status
   */
  getStatus: async (eventId: number) => {
    const response = await apiClient.get(
      `/events/${eventId}/seating/status`
    );
    return response.data;
  }
};
